/**
 * engine/sound.ts — the one audio context of the app: the decks' gains and the scratch voice.
 *
 * Task 45. `spec.md` «Микс и дополнения плеера», «Две деки»: on an iPhone the page cannot set an
 * element's volume (proved by the probe of task 42), so each deck plays through a gain of its own
 * in Web Audio — element → gain → speaker — and an overlap is two gains moving. The context is
 * the one the scratch voice already had (task 18); this module holds it so that both
 * `engine/player.ts` and `engine/scratch.ts` find the same one, whoever made it first.
 *
 * ## Made inside a tap
 *
 * A context is let out of its silence only by a tap, and `navigator.audioSession.type =
 * 'playback'` must come before it, or the silent switch of an iPhone mutes Web Audio. So
 * {@link Sound.wake} does both, in that order; the player calls it from the first tap that plays
 * a track, the scratch from the tap that opens «Сейчас играет». Whoever is second finds it made.
 *
 * ## The mixer (task 56)
 *
 * A deck routed here goes into its chain of the pult's mixer — tone, filter, its gain, MAIN and the
 * meter (`engine/mixer.ts`) — made the first time a deck is routed, in the same context.
 *
 * ## A deck in it never comes out
 *
 * `createMediaElementSource` cannot be undone: from then on the element sounds only through the
 * context, and a context asleep is a deck silent while it «plays». Two rules follow. The player
 * routes the decks only once the context says `running` — never into one that may stay asleep
 * (`engine/player.ts`, «Two decks»). And once they are routed, nothing puts the context to sleep:
 * closing «Сейчас играет» used to suspend it to save the battery, and would now stop the music
 * ({@link Sound.carriesDecks}). The price is a running context for as long as the app plays —
 * `spec.md`, «Примечания»: what that costs a phone's battery is **not checked**.
 */

import type { MediaElementLike } from './element';
import { createMixer } from './mixer';
import type { Mixer, MixerDeck } from './mixer';
import type { ScratchAudio, ScratchContext } from './scratch';
import { webScratchAudio } from './scratch-audio';

export interface Sound {
  /**
   * Inside a tap: `audioSession` first, then the context — made once. `undefined` when the
   * browser refuses one; it is not asked again. Does not resume it: each caller does, in its tap.
   */
  wake(): ScratchContext | undefined;
  /**
   * Routes `el` into the context made — since task 56 into its deck's chain of the pult's mixer
   * (`engine/mixer.ts`). Throws without a context.
   */
  route(el: MediaElementLike): MixerDeck;
  /** Task 56: MAIN's level now, 0…1; 0 before any deck is routed. */
  level(): number;
  /** A deck plays through the context: it must not be put to sleep. */
  readonly carriesDecks: boolean;
}

export function createSound(audio: Pick<ScratchAudio, 'preferPlayback' | 'createContext'>): Sound {
  let context: ScratchContext | undefined;
  let mixer: Mixer | undefined;
  let refused = false;
  let carriesDecks = false;

  return {
    wake(): ScratchContext | undefined {
      if (context !== undefined || refused) return context;
      audio.preferPlayback();
      try {
        context = audio.createContext();
      } catch {
        refused = true;
      }
      return context;
    },
    route(el: MediaElementLike): MixerDeck {
      if (context === undefined) throw new Error('no audio context');
      mixer ??= createMixer(context);
      const deck = mixer.deck(el);
      carriesDecks = true;
      return deck;
    },
    level(): number {
      return mixer?.level() ?? 0;
    },
    get carriesDecks(): boolean {
      return carriesDecks;
    },
  };
}

let instance: Sound | undefined;

/** The app's context, shared by the player and the scratch. */
export function sound(): Sound {
  instance ??= createSound(webScratchAudio());
  return instance;
}
